/**
 * AppHeader — top-of-page chrome.
 *
 * The Figma source has 9 variants (Default / Blocked / Trial / Empty /
 * Small screen / etc.). This first cut implements **`empty`** — the variant
 * used on Sign In, Sign Up, and Password Recovery screens. Other variants
 * follow in subsequent passes; the prop API is shaped so they slot in
 * without renaming.
 *
 * Geometry (Empty):
 *   height 60, side-padding 24, background `surface.background`,
 *   left = logo, right = empty.
 *
 * @format
 */

import {forwardRef} from 'react';

import {styled} from '@mui/material/styles';

import {LogoAssemble} from 'ui/components/molecules/LogoAssemble';

export type AppHeaderVariant = 'empty';
// Other Figma types — wiring up in follow-up passes.
// | 'default'
// | 'trial'
// | 'blocked'
// | 'onboarding'
// | 'smallScreen'
// | 'smallScreenNotifications'
// | 'emptySmallScreen'
// | 'onboardingSmallScreen'

export interface AppHeaderProps {
  variant?: AppHeaderVariant;
  /** Called when the logo is clicked. Typically navigates to /home. */
  onLogoClick?: () => void;
}

const Root = styled('header')(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  height: '60px',
  paddingLeft: theme.spacing(6),
  paddingRight: theme.spacing(6),
  backgroundColor: theme.palette.background.default,
  width: '100%',
  boxSizing: 'border-box',
  // Keep the header above the form column when scrolling.
  position: 'relative',
  zIndex: 2,
}));

const LogoButton = styled('button')(({theme}) => ({
  background: 'transparent',
  border: 'none',
  padding: 0,
  margin: 0,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  // Focus ring is owned by the inner LogoAssemble wrapper for a11y; here
  // we just keep the layout neutral.
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
    borderRadius: 4,
  },
}));

export const AppHeader = forwardRef<HTMLElement, AppHeaderProps>(function AppHeader(
  {variant = 'empty', onLogoClick},
  ref,
) {
  // Only `empty` for now — other variants light up as we implement them.
  if (variant !== 'empty') {
    // eslint-disable-next-line no-console
    console.warn(
      `AppHeader: variant "${variant}" is not implemented yet, falling back to "empty".`,
    );
  }

  return (
    <Root ref={ref}>
      <LogoButton type='button' onClick={onLogoClick} aria-label='AdSensor — home'>
        <LogoAssemble />
      </LogoButton>
    </Root>
  );
});
